import React, { useState, useCallback } from 'react'
import { Box, Typography, IconButton, Tooltip } from '@mui/material'
import { ContentCopy, Check, LocalOffer } from '@mui/icons-material'

import { FlightOffer } from '@/src/types/flightOffer'

interface BannerCardProps {
    offer: FlightOffer
    onOfferClick: (slug: string | undefined) => void
}

export const BannerCard: React.FC<BannerCardProps> = ({ offer, onOfferClick }) => {
    const [copied, setCopied] = useState(false)

    const handleCopyCode = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation()
            if (offer.couponCode) {
                navigator.clipboard.writeText(offer.couponCode)
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
            }
        },
        [offer.couponCode]
    )

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                border: 1,
                borderColor: 'divider',
                bgcolor: 'common.white'
            }}
        >
            {/* Banner Image */}
            <Box
                onClick={() => onOfferClick(offer.slug)}
                sx={{
                    width: '100%',
                    height: '190px',
                    aspectRatio: '16/9',
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: 'grey.100',
                    flexShrink: 0,
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'scale(1.05)'
                    }
                }}
            >
                <Box
                    component="img"
                    src={offer.imageUrl}
                    alt={offer.title || 'Offer banner'}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'fill',
                        objectPosition: 'center',
                        display: 'block'
                    }}
                />
            </Box>

            {/* Banner Content */}
            <Box
                sx={{
                    bgcolor: 'common.white',
                    minHeight: { xs: '100px', md: '110px' },
                    display: 'flex',
                    flexDirection: 'column',
                    px: 2,
                    py: 1.5,
                    flexShrink: 0,
                    justifyContent: 'space-between'
                }}
            >
                {/* Description */}
                <Box
                    sx={{
                        flex: '1 1 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        minHeight: { xs: '42px', md: '42px' }
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: 14,
                            color: 'text.primary',
                            textAlign: 'left',
                            lineHeight: 1.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontWeight: 500
                        }}
                    >
                        {offer.description || ''}
                    </Typography>
                </Box>

                {/* Coupon Code */}
                {offer.couponCode && (
                    <Box
                        sx={{
                            p: { xs: 0.75, md: 1 },
                            pr: { xs: 0.5, md: 0.75 },
                            borderRadius: 1,
                            border: '1px dashed',
                            borderColor: 'primary.main',
                            bgcolor: 'primary.100',
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.5, md: 1 },
                            justifyContent: 'space-between',
                            flexShrink: 0
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
                            <LocalOffer sx={{ fontSize: { xs: 14, md: 16 }, color: 'primary.main' }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: 'primary.main',
                                    fontFamily: 'monospace',
                                    letterSpacing: 1,
                                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                                }}
                            >
                                {offer.couponCode}
                            </Typography>
                        </Box>
                        <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
                            <IconButton
                                size="small"
                                onClick={handleCopyCode}
                                sx={{
                                    p: 0.5,
                                    color: 'primary.main',
                                    '&:hover': {
                                        bgcolor: 'primary.200'
                                    }
                                }}
                            >
                                {copied ? <Check sx={{ fontSize: 16 }} /> : <ContentCopy sx={{ fontSize: 16 }} />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default BannerCard
